/**
 * A field-level difference between two scenario models.
 *
 * Two screens need it and they need the same answer: the version history, where
 * it says what one save changed, and the comparison screen, where it says how
 * two scenarios actually differ. Doing it twice would mean two opinions about
 * whether a renamed package is a rename or a delete-and-add.
 *
 * Pure, like everything else in this directory. It returns *values and their
 * kinds*, never formatted strings — the renderer knows the currency and the
 * theme, this does not. The one exception is enum fields, which resolve to their
 * plain-English label here so that "PERPETUAL" never reaches a reader.
 *
 * Matching is by id, then by name. Ids are stable inside one scenario's history,
 * so a version diff matches on the first pass; two *different* scenarios share
 * no ids at all, so the comparison screen matches on the second. Whatever is
 * left over is an addition or a removal.
 */

import {
  BASIS_META,
  BILLING_MODEL_LABELS,
  CHURN_MODE_LABELS,
  LINE_DIRECTION_LABELS,
  LINE_GROUP_LABELS,
  MAINTENANCE_MODE_LABELS,
} from "./defaults.js";
import { periodLabel } from "./format.js";
import type { LineItemInput, PackageInput, ScenarioModel, YearInput } from "./types.js";

/* ------------------------------------------------------------------- shape */

export type DiffValueKind =
  | "money"
  | "percent"
  | "count"
  | "units"
  /** A duration: "5 years". */
  | "years"
  /** A position in the horizon: "Year 3". */
  | "year"
  | "text"
  | "boolean"
  /** Already plain English — an enum label, or a list of package names. */
  | "label"
  /** The row does not exist on this side at all. */
  | "absent";

export interface DiffValue {
  kind: DiffValueKind;
  value: string | number | boolean | null;
}

export type DiffSection = "settings" | "packages" | "lineItems" | "adoption";

export interface ModelDiffEntry {
  /** Stable across renders and unique within a diff. */
  key: string;
  section: DiffSection;
  /** What the field belongs to: "Scenario", "Starter", "Year 2". */
  subject: string;
  /** The field's name as the editor labels it. */
  field: string;
  change: "added" | "removed" | "changed";
  before: DiffValue;
  after: DiffValue;
}

export interface ModelDiff {
  entries: ModelDiffEntry[];
  /** How many entries each section holds, for the section headings. */
  counts: Record<DiffSection, number>;
  total: number;
}

export const DIFF_SECTION_LABELS: Record<DiffSection, string> = {
  settings: "Scenario settings",
  packages: "Packages",
  lineItems: "Costs & revenues",
  adoption: "Adoption & churn",
};

/* ------------------------------------------------------------------ values */

const absent: DiffValue = { kind: "absent", value: null };
const money = (value: number): DiffValue => ({ kind: "money", value });
const percent = (value: number): DiffValue => ({ kind: "percent", value });
const count = (value: number): DiffValue => ({ kind: "count", value });
const label = (value: string): DiffValue => ({ kind: "label", value });
const text = (value: string | null): DiffValue => ({ kind: "text", value });

/**
 * Money is stored to two decimals and percentages to three, so comparing at six
 * is well clear of both while still ignoring the float noise a round trip
 * through JSON and Decimal leaves behind.
 */
function sameNumber(a: number, b: number): boolean {
  return Math.abs(a - b) < 1e-6;
}

function sameValue(a: DiffValue, b: DiffValue): boolean {
  if (a.kind === "absent" || b.kind === "absent") return a.kind === b.kind;
  if (typeof a.value === "number" && typeof b.value === "number") {
    return sameNumber(a.value, b.value);
  }
  return a.value === b.value;
}

/* ----------------------------------------------------------------- pairing */

interface Pair<T> {
  before: T | undefined;
  after: T | undefined;
  /** What to call the row: the newer name when both sides have one. */
  subject: string;
  key: string;
}

/**
 * Pairs two lists by id, then by case-folded name, leaving the unmatched as
 * additions and removals.
 *
 * A rename therefore reads as a rename inside one scenario's history (the id
 * matches) and as a different package between two scenarios (nothing matches) —
 * which is the honest answer in both cases, because between two scenarios there
 * is nothing to say the two were ever the same thing.
 */
function pairByIdThenName<T extends { id: string; name: string }>(
  before: readonly T[],
  after: readonly T[],
): Pair<T>[] {
  const remaining = new Map<string, T>(before.map((item) => [item.id, item]));
  const byName = new Map<string, T>();
  for (const item of before) {
    const key = item.name.trim().toLowerCase();
    if (!byName.has(key)) byName.set(key, item);
  }

  const pairs: Pair<T>[] = [];

  for (const item of after) {
    let match = remaining.get(item.id);
    if (!match) {
      const candidate = byName.get(item.name.trim().toLowerCase());
      if (candidate && remaining.has(candidate.id)) match = candidate;
    }
    if (match) remaining.delete(match.id);
    pairs.push({ before: match, after: item, subject: item.name, key: item.id });
  }

  // Whatever nobody claimed was removed. Kept in their original order so a diff
  // does not shuffle the list it is describing.
  for (const item of before) {
    if (!remaining.has(item.id)) continue;
    pairs.push({ before: item, after: undefined, subject: item.name, key: item.id });
  }

  return pairs;
}

/* ---------------------------------------------------------------- builders */

class DiffBuilder {
  readonly entries: ModelDiffEntry[] = [];

  compare(
    section: DiffSection,
    key: string,
    subject: string,
    field: string,
    before: DiffValue,
    after: DiffValue,
  ): void {
    if (sameValue(before, after)) return;
    const change =
      before.kind === "absent" ? "added" : after.kind === "absent" ? "removed" : "changed";
    this.entries.push({
      key: `${section}:${key}:${field}`,
      section,
      subject,
      field,
      change,
      before,
      after,
    });
  }
}

function packageValues(pkg: PackageInput): { field: string; value: DiffValue }[] {
  return [
    { field: "Name", value: text(pkg.name) },
    { field: "Billing model", value: label(BILLING_MODEL_LABELS[pkg.billingModel]) },
    { field: "Recurring price", value: money(pkg.recurringPrice) },
    { field: "Recurring cost", value: money(pkg.recurringCost) },
    { field: "Billing period", value: label(periodLabel(pkg.recurringPeriod)) },
    {
      field: pkg.billingModel === "SUBSCRIPTION" ? "Setup fee" : "Licence fee",
      value: money(pkg.oneTimePrice),
    },
    { field: "One-time cost", value: money(pkg.oneTimeCost) },
    { field: "Asset cost", value: money(pkg.assetCost) },
    { field: "Maintenance", value: label(MAINTENANCE_MODE_LABELS[pkg.maintenanceMode]) },
    { field: "Maintenance rate", value: percent(pkg.maintenanceRatePct) },
    { field: "Maintenance amount", value: money(pkg.maintenanceFlatAmount) },
    { field: "Maintenance starts", value: { kind: "year", value: pkg.maintenanceStartYear } },
    { field: "Maintenance attach", value: percent(pkg.maintenanceAttachPct) },
  ];
}

/** Package restrictions read as names, because an id tells a reader nothing. */
function restrictionLabel(item: LineItemInput, names: Map<string, string>): DiffValue {
  if (item.packageIds.length === 0) return label("Every package");
  const resolved = item.packageIds.map((id) => names.get(id) ?? "an unknown package");
  return label([...resolved].sort((a, b) => a.localeCompare(b)).join(", "));
}

function lineItemValues(
  item: LineItemInput,
  names: Map<string, string>,
): { field: string; value: DiffValue }[] {
  const meta = BASIS_META[item.basis];
  return [
    { field: "Name", value: text(item.name) },
    { field: "Direction", value: label(LINE_DIRECTION_LABELS[item.direction]) },
    { field: "Group", value: label(LINE_GROUP_LABELS[item.group]) },
    { field: "Basis", value: label(meta.label) },
    { field: "Amount", value: meta.isPercentage ? percent(item.amount) : money(item.amount) },
    ...(meta.usesUnitsPerClient
      ? [
          {
            field: "Units per client",
            value: { kind: "units" as const, value: item.unitsPerClient },
          },
        ]
      : []),
    ...(meta.usesAttachPct ? [{ field: "Attach rate", value: percent(item.attachPct) }] : []),
    { field: "Starts", value: { kind: "year", value: item.startYear } },
    {
      field: "Ends",
      value:
        item.endYear === null ? label("End of horizon") : { kind: "year", value: item.endYear },
    },
    { field: "Applies to", value: restrictionLabel(item, names) },
  ];
}

/* -------------------------------------------------------------------- main */

export function diffModels(before: ScenarioModel, after: ScenarioModel): ModelDiff {
  const builder = new DiffBuilder();

  /* settings */
  const settings: [string, DiffValue, DiffValue][] = [
    ["Currency", label(before.currencyCode), label(after.currencyCode)],
    [
      "Horizon",
      { kind: "years", value: before.horizonYears },
      { kind: "years", value: after.horizonYears },
    ],
    [
      "Commitment",
      { kind: "years", value: before.commitmentYears },
      { kind: "years", value: after.commitmentYears },
    ],
    ["Discount rate", percent(before.discountRatePct), percent(after.discountRatePct)],
    [
      "Reuse assets",
      { kind: "boolean", value: before.reuseAssets },
      { kind: "boolean", value: after.reuseAssets },
    ],
  ];
  for (const [field, a, b] of settings) {
    builder.compare("settings", "scenario", "Scenario", field, a, b);
  }

  /* packages */
  const packagePairs = pairByIdThenName(before.packages, after.packages);
  for (const pair of packagePairs) {
    if (!pair.before || !pair.after) {
      const present = (pair.after ?? pair.before)!;
      builder.compare(
        "packages",
        pair.key,
        present.name,
        "Package",
        pair.before ? label(pair.before.name) : absent,
        pair.after ? label(pair.after.name) : absent,
      );
      continue;
    }
    const left = packageValues(pair.before);
    const right = packageValues(pair.after);
    right.forEach((entry, index) => {
      const other = left[index];
      if (!other) return;
      builder.compare("packages", pair.key, pair.subject, entry.field, other.value, entry.value);
    });
  }

  /* line items */
  const namesBefore = new Map(before.packages.map((pkg) => [pkg.id, pkg.name]));
  const namesAfter = new Map(after.packages.map((pkg) => [pkg.id, pkg.name]));
  const linePairs = pairByIdThenName(before.lineItems, after.lineItems);
  for (const pair of linePairs) {
    if (!pair.before || !pair.after) {
      const present = (pair.after ?? pair.before)!;
      builder.compare(
        "lineItems",
        pair.key,
        present.name,
        "Line",
        pair.before ? label(pair.before.name) : absent,
        pair.after ? label(pair.after.name) : absent,
      );
      continue;
    }
    const left = lineItemValues(pair.before, namesBefore);
    const right = lineItemValues(pair.after, namesAfter);
    // The two sides can list different fields — a basis change swaps "attach
    // rate" for "units per client" — so they are matched by field name, not by
    // position, and a field only one side has is reported as added or removed.
    const leftByField = new Map(left.map((entry) => [entry.field, entry.value]));
    const rightByField = new Map(right.map((entry) => [entry.field, entry.value]));
    for (const field of new Set([...leftByField.keys(), ...rightByField.keys()])) {
      builder.compare(
        "lineItems",
        pair.key,
        pair.subject,
        field,
        leftByField.get(field) ?? absent,
        rightByField.get(field) ?? absent,
      );
    }
  }

  /* adoption */
  const yearCount = Math.max(before.years.length, after.years.length);
  const pairedPackages = packagePairs.filter((pair) => pair.before && pair.after);
  for (let index = 0; index < yearCount; index += 1) {
    const yearNumber = index + 1;
    const subject = `Year ${yearNumber}`;
    const key = `year-${yearNumber}`;
    const left: YearInput | undefined = before.years[index];
    const right: YearInput | undefined = after.years[index];

    if (!left || !right) {
      builder.compare(
        "adoption",
        key,
        subject,
        "Year",
        left ? count(left.newClients) : absent,
        right ? count(right.newClients) : absent,
      );
      continue;
    }

    builder.compare(
      "adoption",
      key,
      subject,
      "New clients",
      count(left.newClients),
      count(right.newClients),
    );

    for (const pair of pairedPackages) {
      const beforeCount = left.mix[pair.before!.id] ?? 0;
      const afterCount = right.mix[pair.after!.id] ?? 0;
      builder.compare(
        "adoption",
        key,
        subject,
        `${pair.subject} signings`,
        count(beforeCount),
        count(afterCount),
      );
    }

    builder.compare(
      "adoption",
      key,
      subject,
      "Churn measured as",
      label(CHURN_MODE_LABELS[left.churnMode]),
      label(CHURN_MODE_LABELS[right.churnMode]),
    );
    // Only the figure the mode actually reads: a churn count left over from
    // before someone switched to a rate is not a difference anybody can see.
    if (left.churnMode === "COUNT" || right.churnMode === "COUNT") {
      builder.compare(
        "adoption",
        key,
        subject,
        "Churn",
        count(left.churnCount),
        count(right.churnCount),
      );
    }
    if (left.churnMode === "RATE" || right.churnMode === "RATE") {
      builder.compare(
        "adoption",
        key,
        subject,
        "Churn rate",
        percent(left.churnRatePct),
        percent(right.churnRatePct),
      );
    }
    builder.compare(
      "adoption",
      key,
      subject,
      "Note",
      text(left.note ?? null),
      text(right.note ?? null),
    );
  }

  const counts: Record<DiffSection, number> = {
    settings: 0,
    packages: 0,
    lineItems: 0,
    adoption: 0,
  };
  for (const entry of builder.entries) counts[entry.section] += 1;

  return { entries: builder.entries, counts, total: builder.entries.length };
}
